import {ChangeDetectionStrategy, Component, InputSignal, OutputEmitterRef, Signal, computed, input, output} from '@angular/core';
import {ContentComponent} from '../../shared/elements/content/content.component';
import {FormButtonComponent} from '../../shared/elements/form-button/form-button.component';
import {HeadlineComponent} from '../../shared/elements/headline/headline.component';
import {IconBadgeComponent} from '../../shared/elements/icon-badge/icon-badge.component';
import {RichTextClassMapPipe} from '../../shared/pipes/rich-text-class-map.pipe';
import {TranslatePipe} from '../../shared/pipes/translate.pipe';
import {FeedbackCompletionMode} from '../../shared/types/generated/feedbackCompletionMode';
import {FeedbackCompletionResultDisplay} from '../../shared/types/generated/feedbackCompletionResultDisplay';
import {FeedbackAnswerEvaluationResource} from '../../shared/types/generated/feedbackAnswerEvaluationResource';
import {FeedbackAttemptCompletionResource} from '../../shared/types/generated/feedbackAttemptCompletionResource';
import {FeedbackFormCompletionResource} from '../../shared/types/generated/feedbackFormCompletionResource';
import {FeedbackStructureQuestionFormQuestion} from './feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {
  normalizeCompletionMode,
  normalizeCompletionResultDisplay,
  normalizeCompletionText,
} from './feedback-attempt-completion.utils';

@Component({
  selector: 'app-feedback-attempt-completion',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ContentComponent,
    FormButtonComponent,
    HeadlineComponent,
    IconBadgeComponent,
    RichTextClassMapPipe,
    TranslatePipe,
  ],
  template: `
    <div class="card mb-3">
      <div class="card-body p-card text-center">
        <div class="no-content-large-icon rounded-circle d-flex justify-content-center align-items-center mx-auto" [class.bg-success]="isSuccessState()" [class.bg-primary]="!isSuccessState()">
          <app-icon-badge
            [style]="'default'"
            [icon]="isSuccessState() ? 'check' : 'cycle'"
            [fontSize]="'fs-3'"
            [fontColor]="'text-white'"
          ></app-icon-badge>
        </div>

        <app-headline
          [title]="titleKey() | translate"
          [type]="'h4'"
          [class]="'mt-3'"
        ></app-headline>

        @if (publicMode()) {
          <div class="mt-2">
            <app-content [fontColor]="'text-muted'">{{ 'FEEDBACKPUBLICSUBMITTEDSUCCESSFULLY' | translate }}</app-content>
          </div>
          <div class="mt-2">
            <app-content [fontColor]="'text-muted'">{{ 'FEEDBACKPUBLICSAFETOCLOSE' | translate }}</app-content>
          </div>
        } @else if (showsEvaluation()) {
          <div class="d-flex justify-content-center align-items-center flex-wrap my-4 gap-2">
            <app-headline [title]="'FEEDBACKSCORERESULT' | translate" [type]="'h5'" [class]="headlineClass()"></app-headline>
            @if (resultDisplay() === FeedbackCompletionResultDisplay.STATUS_ONLY) {
              <app-headline [title]="statusLabelKey() | translate" [type]="'h5'" [class]="headlineClass()"></app-headline>
            } @else {
              <app-headline [title]="displayResultValue()" [type]="'h5'" [class]="headlineClass()"></app-headline>
              @if (resultDisplay() === FeedbackCompletionResultDisplay.PERCENTAGE) {
                <app-headline [title]="'%'" [type]="'h5'" [class]="headlineClass()"></app-headline>
              } @else {
                <app-headline [title]="'FEEDBACKSCOREPREFIX' | translate" [type]="'h5'" [class]="headlineClass()"></app-headline>
              }
            }
          </div>
          <app-content
            [type]="'richtext'"
            [expandable]="false"
            [innerHTML]="(bodyHtml() ?? '') | richTextClassMap"
          ></app-content>
        } @else {
          <div class="mt-4">
            <app-content
              [type]="'richtext'"
              [expandable]="false"
              [innerHTML]="(bodyHtml() ?? '') | richTextClassMap"
            ></app-content>
          </div>
        }
      </div>

      @if (showReturnButton() || publicMode()) {
        <div class="card-footer">
          <div class="d-flex justify-content-center">
            @if (publicMode()) {
              <app-form-button
                [text]="'FEEDBACKPUBLICCLOSETAB' | translate"
                [cssClass]="'e-primary min-w-auto'"
                (click)="closeTabSelected.emit()"
              ></app-form-button>
            } @else {
              <app-form-button
                [text]="'BACK' | translate"
                [cssClass]="'e-primary min-w-auto'"
                (click)="returnSelected.emit()"
              ></app-form-button>
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class FeedbackAttemptCompletionComponent {
  public FeedbackCompletionResultDisplay: typeof FeedbackCompletionResultDisplay = FeedbackCompletionResultDisplay;

  public completion: InputSignal<FeedbackFormCompletionResource | null> = input<FeedbackFormCompletionResource | null>(null);
  public questions: InputSignal<FeedbackStructureQuestionFormQuestion[]> = input<FeedbackStructureQuestionFormQuestion[]>([]);
  public completionResult: InputSignal<FeedbackAttemptCompletionResource | null> = input<FeedbackAttemptCompletionResource | null>(null);
  public evaluations: InputSignal<FeedbackAnswerEvaluationResource[]> = input<FeedbackAnswerEvaluationResource[]>([]);
  public publicMode: InputSignal<boolean> = input(false);
  public showReturnButton: InputSignal<boolean> = input(false);
  public returnSelected: OutputEmitterRef<void> = output();
  public closeTabSelected: OutputEmitterRef<void> = output();

  public completionMode: Signal<FeedbackCompletionMode> = computed(() => normalizeCompletionMode(this.completion()));
  public resultDisplay: Signal<FeedbackCompletionResultDisplay> = computed(() => normalizeCompletionResultDisplay(this.completion()));
  public achievedScore: Signal<number> = computed(() => this.completionResult()?.score ?? this.evaluations().reduce((sum, evaluation) => sum + evaluation.score, 0));
  public maximumScore: Signal<number> = computed(() => this.completionResult()?.maximum_score ?? 0);
  public passed: Signal<boolean> = computed(() => {
    if (this.completionMode() !== FeedbackCompletionMode.EVALUATION) {
      return true;
    }

    return this.completionResult()?.passed ?? false;
  });
  public displayResultValue: Signal<string> = computed(() => {
    const achievedScore = this.achievedScore();
    const maximumScore = this.maximumScore();

    if (this.resultDisplay() === FeedbackCompletionResultDisplay.SCORE) {
      return Number(achievedScore.toFixed(2)).toString();
    }

    if (this.resultDisplay() === FeedbackCompletionResultDisplay.PERCENTAGE) {
      const percentage = maximumScore > 0 ? (achievedScore / maximumScore) * 100 : 0;
      return Number(percentage.toFixed(2)).toString();
    }

    return Number(achievedScore.toFixed(2)).toString();
  });
  public bodyHtml: Signal<string | null> = computed(() => {
    if (this.publicMode()) {
      return null;
    }

    if (this.completionMode() === FeedbackCompletionMode.EVALUATION) {
      return this.passed()
        ? normalizeCompletionText(this.completion()?.text)
        : normalizeCompletionText(this.completion()?.failed_text);
    }

    return normalizeCompletionText(this.completion()?.text);
  });

  public showsEvaluation(): boolean {
    return !this.publicMode() && this.completionMode() === FeedbackCompletionMode.EVALUATION;
  }

  public isSuccessState(): boolean {
    return this.publicMode() || !this.showsEvaluation() || this.passed();
  }

  public titleKey(): string {
    if (this.publicMode()) {
      return 'FEEDBACKCOMPLETED';
    }

    return this.showsEvaluation()
      ? this.statusLabelKey()
      : 'FEEDBACKCOMPLETED';
  }

  public statusLabelKey(): string {
    return this.passed() ? 'FEEDBACKSCOREWIN' : 'FEEDBACKSCORELOSS';
  }

  public headlineClass(): string {
    return this.passed() ? 'text-primary fw-bold' : 'text-danger fw-bold';
  }
}
